import React from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FuneralHome } from "../listing-page.types";

interface CompareWidgetProps {
    compareList: FuneralHome[];
    onRemove: (id: number) => void;
    onCompare: () => void;
    onClose: () => void;
}

const CompareWidget: React.FC<CompareWidgetProps> = ({
    compareList,
    onRemove,
    onCompare,
    onClose,
}) => {
    if (compareList.length === 0) return null;

    const getImageUrl = (item: FuneralHome) => {
        const fileInfo = item.fileInfo || (item as any).file_info;
        if (!fileInfo || !Array.isArray(fileInfo)) return "";
        const image = fileInfo.find((f: any) => {
            const mediaType = (f.mediaType || f.media_type)?.toLowerCase();
            return mediaType === "image" && (f.url || f.path);
        });
        const url = (image as any)?.url || (image as any)?.path || (fileInfo[0] as any)?.url || (fileInfo[0] as any)?.path || "";
        return url;
    };

    return (
        <Paper
            elevation={6}
            sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                width: 320,
                zIndex: 1300,
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
            }}
        >
            <Box
                sx={{
                    p: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#fff",
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#104145" }}>
                    Compare List{" "}
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: "#104145",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            ml: 1,
                        }}
                    >
                        {compareList.length}
                    </Box>
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider />

            <Box
                sx={{
                    p: 1,
                    maxHeight: 170, // Set height to fit approximately 2 items
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                        width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#e0e0e0",
                        borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                {compareList.map((item) => (
                    <Box
                        key={item.id}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1,
                            mb: 1,
                            "&:last-child": { mb: 0 },
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9",
                            position: "relative",
                            minHeight: "72px"
                        }}
                    >
                        <Box
                            component="img"
                            src={getImageUrl(item)}
                            alt={item.businessName}
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "4px",
                                objectFit: "cover",
                                mr: 1.5,
                                backgroundColor: "#f0f0f0"
                            }}
                            onError={(e: any) => {
                                (e.target as any).src = "https://via.placeholder.com/50?text=No+Img";
                            }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: "#104145",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {item.businessName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ display: "block", color: "#757575" }}
                            >
                                {item.location}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: "#104145" }}
                            >
                                ${item.startingPrice}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => onRemove(item.id)}
                            sx={{ ml: 1, p: 0.5 }}
                        >
                            <CloseIcon sx={{ fontSize: "14px", color: "#9e9e9e" }} />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            <Box sx={{ p: 1.5 }}>
                <Button
                    fullWidth
                    variant="contained"
                    disabled={compareList.length === 0}
                    onClick={onCompare}
                    sx={{
                        backgroundColor: "#ebd28d",
                        color: "#104145",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": {
                            backgroundColor: "#dcc17b",
                            boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#f5f5f5",
                            color: "#bdbdbd",
                        }
                    }}
                >
                    Compare ({compareList.length})
                </Button>
            </Box>
        </Paper>
    );
};

export default CompareWidget;
